import { Box, Typography, IconButton, Skeleton, CircularProgress } from '@mui/material';
import { EasTooltip } from 'ui/EasTooltip';
import { Help, KeyboardBackspace, LinkOutlined, OpenInNew } from '@mui/icons-material';
import { EasTableValue } from 'ui/EasTable/EasTableRow/EasTableValue';
import { ETableStructureType } from 'ui/EasTable/types';
import { UserAvatar } from 'ui/UserAvatar';
import { TUser } from 'redux/reducers/user/types';
import { TEmManagementByIdFullResponse } from 'redux/reducers/emManagement/types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import {
	getEmByIdFull,
	getEmByIdFullLoading,
	getEmPaymentMethodsData,
	getSpecialRegistrations,
	getSpecialRegistrationsLoading,
} from 'redux/reducers/emManagement/selectors';
import { PATHS } from 'routes/constants';
import { useGetCountryById } from 'utils/getCountry';
import {
	getEmByIdFullRequest,
	getEmPaymentMethodsRequest,
	getSpecialRegistrationsRequest,
} from 'redux/reducers/emManagement/reducer';
import { DateTime } from 'luxon';
import { TSavedUrl } from '../../../../redux/reducers/auth/types';
import { ACTIVE_SERVICES_FILTER_OPTIONS } from '../../constants';

export const EMManagementIdHeader = ({
	savedUrl,
	emIdProp,
	showBackButton = true,
}: {
	savedUrl: TSavedUrl;
	emIdProp?: number;
	showBackButton?: boolean;
}) => {
	const navigate = useNavigate();
	// const savedUrl = useSelector(getSavedUrl);
	// const savedUrl2 = useSelector(getSavedUrl2);

	const emDataFull = useSelector(getEmByIdFull);
	const emDataFullLoading = useSelector(getEmByIdFullLoading);
	const specialRegistrations = useSelector(getSpecialRegistrations);
	const specialRegistrationsLoading = useSelector(getSpecialRegistrationsLoading);
	const emPaymentMethods = useSelector(getEmPaymentMethodsData);
	const dispatch = useDispatch();
	const emIdParams = useParams<{ emId?: string }>().emId ?? undefined;
	const emId = emIdProp ?? emIdParams ?? undefined;
	const registeredCountry = useGetCountryById(
		emDataFull?.registeredCountry && emDataFull.registeredCountry > 0
			? emDataFull.registeredCountry
			: 0,
	);

	const handleReplayHistory = () => {
		if (savedUrl && savedUrl !== window.location.pathname) {
			if (savedUrl.includes(PATHS.EM_MANAGEMENT_PAGE)) navigate(savedUrl);
		} else navigate(PATHS.EM_MANAGEMENT_PAGE);
	};

	const handleOpenInNew = () => {
		if (showBackButton) return;
		window.open(`/em-management/${emId}`, '_blank');
	};

	useEffect(() => {
		if (emId) {
			dispatch(getEmByIdFullRequest({ id: Number(emId) }));
			dispatch(getEmPaymentMethodsRequest({ emId: Number(emId) }));
			dispatch(getSpecialRegistrationsRequest({ emId: Number(emId) }));
		}
	}, [emId, dispatch]);

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
			{(emDataFullLoading || specialRegistrationsLoading) && (
				<Skeleton variant="rectangular" width="800px" height="30px" sx={{ borderRadius: '12px' }} />
			)}
			{emDataFull && !specialRegistrationsLoading && specialRegistrations && (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
					{showBackButton && (
						<IconButton onClick={handleReplayHistory} color="primary" sx={{ p: 0 }}>
							<KeyboardBackspace />
						</IconButton>
					)}
					<EasTooltip title="Full Name">
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={handleOpenInNew}>
							{!showBackButton && <OpenInNew color="primary" sx={{ fontSize: '1rem' }} />}
							<Typography id="em-name" variant="h5" color="primary">
								{emDataFull?.fullName ?? ''}
							</Typography>
						</Box>
					</EasTooltip>
					{/* <Chip
		label={`ID: ${emData?.id?.toString() ?? ''}`}
		size="small"
		sx={{
			p: '0 5px',
			ml: 1,
			mr: 1,
			fontSize: '.9rem',
			height: '20px',
			backgroundColor: 'primary.light',
			color: 'primary.main',
			fontWeight: 'bold',
		}}
		/>
		*/}
					<Box component="span" id="em-id">
						<EasTableValue
							value={`EM Id: ${emDataFull?.id?.toString() ?? ''}`}
							type={ETableStructureType.STATUS}
							size="m"
							tableStructureItem={{
								filter: [['Active', 'Active']],
								colors: [['Active', 'success.light']],
							}}
							tooltip="EM Id"
						/>
					</Box>
					{registeredCountry?.isoAlpha2 && (
						<Box component="span" id="em-registered-country">
							<EasTableValue
								value={registeredCountry?.isoAlpha2}
								type={ETableStructureType.COUNTRY}
								size="m"
								tooltip="Registered Country"
								chip
							/>
						</Box>
					)}

					<Box component="span" id="em-created-date">
						<EasTableValue
							value={
								emDataFull?.created
									? DateTime.fromISO(emDataFull.created).toFormat('yyyy-MM-dd')
									: ''
							}
							type={ETableStructureType.STATUS}
							size="m"
							tooltip="Created Date"
						/>
					</Box>

					{/* <Box
						component="span"
						id="em-active-services"
						sx={{
							'&:hover': {
								backgroundColor: '#01619320',
								padding: '2px 2px',
								margin: '0px -2px',
								borderRadius: '12px',
							},
						}}
					> */}
					<Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
						{emDataFull?.activeServices.map((service, index) => (
							<Box
								key={index}
								component="span"
								onClick={() => {
									if (service === 'GPSR') {
										window.open(`/gpsr?search=${emDataFull.fullName}`, '_blank');
									}
								}}
							>
								<EasTableValue
									value={service}
									type={ETableStructureType.STATUS}
									size="m"
									tooltip="Active Services"
									tableStructureItem={{
										filter: ACTIVE_SERVICES_FILTER_OPTIONS,
									}}
								/>
							</Box>
						))}
					</Box>
					{/* </Box> */}
					<Box component="span" id="em-legal-status">
						<EasTableValue
							value={emDataFull?.legalStatus}
							type={ETableStructureType.STATUS}
							tableStructureItem={{
								filter: [
									['not_established_in_eu', 'Not established in EU'],
									['established_in_eu', 'Established in EU'],
								],
								// colors: [
								// 	['not_established_in_eu', 'warning.light'],
								// 	['established_in_eu', 'info.light'],
								// ],
							}}
							size="m"
							tooltip="Legal Status"
						/>
					</Box>

					{!specialRegistrationsLoading &&
						specialRegistrations &&
						specialRegistrations.hmrcConnected !== null && (
							<Box
								component="span"
								id="hmrc-status"
								onClick={() => {
									if (specialRegistrations.hmrcConnected) {
										window.open(`/hmrc?search=${emDataFull.fullName}`, '_blank');
									}
								}}
							>
								<EasTableValue
									value={specialRegistrations.hmrcConnected ? 'Connected' : 'Not Connected'}
									type={ETableStructureType.STATUS}
									size="m"
									tableStructureItem={{
										filter: [
											['Connected', 'HMRC'],
											['Not Connected', 'HMRC'],
										],
										colors: [
											['Connected', 'success.light'],
											['Not Connected', 'error.light'],
										],
									}}
									tooltip={
										specialRegistrations.hmrcConnected ? (
											<div>
												HMRC connected to EAS
												<br />
												Press to open in new tab
											</div>
										) : (
											'HMRC not connected to EAS'
										)
									}
								/>
							</Box>
						)}
					{emPaymentMethods && emPaymentMethods.length > 0 && (
						<Box component="span" id="em-payment-method">
							<EasTableValue
								value={emPaymentMethods[0].type}
								type={ETableStructureType.STATUS}
								size="m"
								tooltip="Payment Method"
							/>
						</Box>
					)}

					<Box component="span" id="em-status">
						<EasTableValue
							value={emDataFull?.status ?? ''}
							type={ETableStructureType.STATUS}
							size="m"
							tableStructureItem={{
								filter: [
									['Active', 'Active'],
									['Blocked', 'Blocked'],
								],
								colors: [
									['Active', 'success.light'],
									['Blocked', 'error.light'],
								],
							}}
							tooltip="Status"
						/>
					</Box>

					<UserAvatar user={emDataFull?.supportManager ?? ({} as TUser)} size="xxs" />
					{emDataFullLoading && <CircularProgress size={20} />}
				</Box>
			)}
			{/* <Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					gap: 1,
					mt: 1,
					flexWrap: 'wrap',
					maxWidth: '900px',
				}}
			>
				{emDataFull?.sites?.map((site: TEmSites) => (
					<Box
						key={`${site.webAddress}-${site.integrationType}-${site.sitePlatform ?? 'none'}-${site.mainSite ? 'main' : 'alt'}`}
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 0.5,
							lineHeight: '1',
							border: site.mainSite ? '2px solid #3498db' : '1.5px solid #bdd2df',
							borderRadius: '12px',
							padding: '1px 4px',
							justifyContent: 'center',
							textAlign: 'center',
							// backgroundColor: site.mainSite ? '#01619320' : 'transparent',
						}}
					>
						{site.sitePlatform ? (
							<EasTooltip title={site.sitePlatform}>
								<img
									src={EmSitePlatformImages[site.sitePlatform as EEmSitePlatform]}
									alt={site.sitePlatform ?? ''}
									// width={30}
									height={14}
									style={{
										// width: '30px',
										// height: '30px',
										objectFit: 'contain',
									}}
								/>
							</EasTooltip>
						) : (
							<EasTooltip title="No Platform">
								<Help />
							</EasTooltip>
						)}
						<Typography
							variant="body1"
							color="primary"
							component="a"
							target="_blank"
							href={site.webAddress}
							sx={{
								fontSize: '0.9rem',
								fontWeight: 'bold',
								color: 'primary.main',
								textDecoration: 'none',
								cursor: 'pointer',
							}}
						>
							{site.webAddress ?? 'N/A'}
						</Typography>{' '}
						{site.sitePlatform && site.integrationType === EEmSiteIntegrationType.API ? (
							<Typography
								variant="body2"
								color="primary"
								component="span"
								fontFamily="monospace"
								fontWeight="bold"
								sx={{
									fontSize: '0.7rem',
									backgroundColor: 'primary.light',
									padding: '0px 2px',
									borderRadius: '12px',
								}}
							>
								api
							</Typography>
						) : site.integrationType === EEmSiteIntegrationType.PLUGIN ? (
							<Typography
								variant="body2"
								color="primary"
								component="span"
								fontFamily="monospace"
								fontWeight="bold"
								sx={{
									fontSize: '0.7rem',
									backgroundColor: 'primary.light',
									padding: '0px 2px',
									borderRadius: '12px',
								}}
							>
								plugin
							</Typography>
						) : site.integrationType === EEmSiteIntegrationType.CSV ? (
							<Typography
								variant="body2"
								color="primary"
								component="span"
								fontFamily="monospace"
								fontWeight="bold"
								sx={{
									fontSize: '0.7rem',
									backgroundColor: 'primary.light',
									padding: '0px 2px',
									borderRadius: '12px',
								}}
							>
								csv
							</Typography>
						) : (
							<Typography
								variant="body2"
								component="span"
								fontFamily="monospace"
								fontWeight="bold"
								sx={{
									fontSize: '0.7rem',
									backgroundColor: 'warning.light',
									color: 'warning.dark',
									padding: '0px 8px',
									borderRadius: '12px',
								}}
							>
								?
							</Typography>
						)}
					</Box>
				))}
			</Box> */}
		</Box>
	);
};
